import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import RawHeader from "../components/RawHeader";
import AdminNav from "../components/AdminNav";
import LogoCanvas from "../components/LogoCanvas";

const AdminLogoStudio = () => {
  const [logos, setLogos] = useState([]);
  const [patterns, setPatterns] = useState([]);
  const [prompt, setPrompt] = useState("");
  const [aiImage, setAiImage] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [logoState, setLogoState] = useState(null);
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("ai"); // ai | manual | pattern

  const load = async () => {
    try {
      const [l, p] = await Promise.all([api.get("/logos"), api.get("/patterns")]);
      setLogos(l.data);
      setPatterns(p.data);
    } catch {}
  };
  useEffect(() => { load(); }, []);

  const generate = async () => {
    if (!prompt.trim()) return toast.error("Describe it first");
    setGenerating(true);
    setAiImage(null);
    try {
      const style = activeTab === "pattern" ? "pattern" : "logo";
      const r = await api.post("/ai/generate-image", { prompt, style });
      setAiImage(`data:${r.data.mime_type};base64,${r.data.image_base64}`);
      toast.success(style === "pattern" ? "Pattern generated" : "Logo generated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generation failed");
    } finally {
      setGenerating(false);
    }
  };

  const save = async () => {
    let image_base64;
    if (activeTab === "manual" && logoState?._export) {
      image_base64 = logoState._export();
    } else if (aiImage) {
      image_base64 = aiImage;
    } else {
      return toast.error("Create something first");
    }
    if (!name.trim()) return toast.error("Name it first");
    setSaving(true);
    try {
      const endpoint = activeTab === "pattern" ? "/patterns" : "/logos";
      await api.post(endpoint, { name, image_base64, notes: prompt || "Manual design" });
      toast.success("Saved");
      setName("");
      setAiImage(null);
      setPrompt("");
      load();
    } catch (e) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const del = async (id, kind) => {
    if (!window.confirm(`Delete this ${kind}?`)) return;
    try {
      await api.delete(`/${kind}s/${id}`);
      toast.success("Deleted");
      load();
    } catch {}
  };

  const tabs = [
    { id: "ai", label: "AI Logo" },
    { id: "manual", label: "Manual Editor" },
    { id: "pattern", label: "AI Patterns" },
  ];

  return (
    <div className="min-h-screen text-white">
      <RawHeader variant="admin" />
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-10 py-8">
        <AdminNav />
        <div className="mb-8">
          <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-2">// BRAND_IDENTITY</div>
          <h1 className="font-display text-4xl">LOGO & PATTERN STUDIO</h1>
          <p className="text-[11px] tracking-[0.2em] uppercase text-zinc-600 mt-2">
            Design logos, generate AI patterns, and build your brand asset library
          </p>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-zinc-800 mb-6 overflow-x-auto">
          {tabs.map((t) => (
            <button
              key={t.id}
              data-testid={`tab-${t.id}`}
              onClick={() => { setActiveTab(t.id); setAiImage(null); setPrompt(""); }}
              className={`px-6 py-3 text-[11px] tracking-[0.25em] uppercase font-bold border-b-2 transition-colors whitespace-nowrap ${
                activeTab === t.id ? "border-[#d4af37] text-white" : "border-transparent text-zinc-500 hover:text-white"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Content */}
        {activeTab === "manual" ? (
          <LogoCanvas value={logoState} onChange={setLogoState} />
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_1fr] gap-6">
            <div className="space-y-4">
              <div>
                <label className="label-raw">
                  {activeTab === "pattern" ? "Describe The Pattern" : "Describe Your Logo"}
                </label>
                <textarea
                  data-testid="ai-gen-prompt"
                  rows={6}
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  placeholder={activeTab === "pattern"
                    ? "e.g. Seamless red camo pattern on black, urban military streetwear"
                    : "e.g. Bold minimalist wordmark with industrial edge"}
                  className="input-raw resize-none"
                />
              </div>
              {activeTab === "pattern" && (
                <div className="border border-zinc-800 bg-[#0a0a0a] p-4">
                  <div className="text-[9px] tracking-[0.3em] uppercase text-[#d4af37] mb-2">// PATTERN_IDEAS</div>
                  <div className="grid grid-cols-2 gap-2 text-[10px] tracking-[0.1em]">
                    {[
                      "Red & black checkerboard, skate vibe",
                      "Tiger camouflage, military streetwear",
                      "Abstract graffiti tag repeat, black white red",
                      "Retro 90s geometric shapes, pastel",
                      "Distressed barcode stripes, mono",
                      "Floral Japanese tattoo style, monochrome",
                    ].map((s) => (
                      <button
                        key={s}
                        onClick={() => setPrompt(s)}
                        className="border border-zinc-800 p-2 text-left text-zinc-400 hover:border-[#d4af37] hover:text-white transition-colors"
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <button data-testid="ai-generate-btn" onClick={generate} disabled={generating} className="btn-raw btn-raw-primary w-full">
                {generating ? "GENERATING..." : activeTab === "pattern" ? "→ GENERATE PATTERN" : "→ GENERATE LOGO"}
              </button>
            </div>
            <div
              className="border border-zinc-800 bg-[#0a0a0a] aspect-square relative grid-bg"
              data-testid="ai-preview"
              style={activeTab === "pattern" && aiImage ? {
                backgroundImage: `url(${aiImage})`,
                backgroundSize: "150px",
                backgroundRepeat: "repeat",
              } : {}}
            >
              {aiImage && activeTab !== "pattern" ? (
                <img src={aiImage} alt="" className="absolute inset-0 w-full h-full object-contain" />
              ) : !aiImage ? (
                <div className="absolute inset-0 flex items-center justify-center text-zinc-600 text-xs tracking-[0.3em] uppercase">
                  // PREVIEW
                </div>
              ) : null}
              {activeTab === "pattern" && aiImage && (
                <div className="absolute bottom-3 left-3 text-[9px] tracking-[0.3em] uppercase text-white font-mono bg-black/70 px-2 py-1">
                  // TILED_PREVIEW
                </div>
              )}
            </div>
          </div>
        )}

        {/* Save */}
        <div className="mt-8 border border-zinc-800 bg-[#0a0a0a] p-5 flex flex-col sm:flex-row items-stretch sm:items-end gap-3">
          <div className="flex-1">
            <label className="label-raw">{activeTab === "pattern" ? "Pattern Name" : "Logo Name"}</label>
            <input
              data-testid="asset-name"
              className="input-raw"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={activeTab === "pattern" ? "RED_CAMO_01" : "MAIN_WORDMARK_V1"}
            />
          </div>
          <button data-testid="save-asset" onClick={save} disabled={saving} className="btn-raw btn-raw-primary sm:w-52">
            {saving ? "SAVING..." : `SAVE ${activeTab === "pattern" ? "PATTERN" : "LOGO"}`}
          </button>
        </div>

        {/* Libraries */}
        {logos.length > 0 && (
          <div className="mt-12">
            <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-4">// LOGO_LIBRARY · {logos.length}</div>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-px bg-zinc-800 border border-zinc-800">
              {logos.map((l) => (
                <div key={l.id} className="bg-[#050505] p-3" data-testid={`logo-${l.id}`}>
                  <div className="aspect-square bg-[#0a0a0a] border border-zinc-800 mb-2 grid-bg relative">
                    <img src={l.image_base64} alt={l.name} className="absolute inset-0 w-full h-full object-contain" />
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="font-mono text-[11px] truncate">{l.name}</div>
                    <button onClick={() => del(l.id, "logo")} className="text-[9px] tracking-[0.2em] uppercase text-zinc-600 hover:text-[#d4af37]">DEL</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {patterns.length > 0 && (
          <div className="mt-12">
            <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-4">// PATTERN_LIBRARY · {patterns.length}</div>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-px bg-zinc-800 border border-zinc-800">
              {patterns.map((p) => (
                <div key={p.id} className="bg-[#050505] p-3" data-testid={`pattern-${p.id}`}>
                  <div
                    className="aspect-square border border-zinc-800 mb-2"
                    style={{
                      backgroundImage: `url(${p.image_base64})`,
                      backgroundSize: "80px",
                      backgroundRepeat: "repeat",
                    }}
                  />
                  <div className="flex items-center justify-between">
                    <div className="font-mono text-[11px] truncate">{p.name}</div>
                    <button onClick={() => del(p.id, "pattern")} className="text-[9px] tracking-[0.2em] uppercase text-zinc-600 hover:text-[#d4af37]">DEL</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminLogoStudio;
